import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Wallet, 
  Plus, 
  Search,
  Calendar,
  MoreVertical,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';
import SARSymbol from '../components/SARSymbol';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency display helper
const CurrencyValue = ({ value, symbol, isSAR, size = 14 }) => {
  if (isSAR) {
    return (
      <span className="inline-flex items-center gap-1">
        <SARSymbol size={size} />
        <span>{value}</span>
      </span>
    );
  }
  return <span>{symbol} {value}</span>;
};

const Expenses = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol, isSAR } = useSettings();
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({});
  const [filterCategory, setFilterCategory] = useState('all');

  const statusColors = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-blue-50 text-blue-700 border-blue-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    reimbursed: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };

  const statusLabels = {
    pending: isRTL ? 'قيد الانتظار' : 'Pending',
    approved: isRTL ? 'موافق عليه' : 'Approved',
    rejected: isRTL ? 'مرفوض' : 'Rejected',
    reimbursed: isRTL ? 'تم السداد' : 'Reimbursed'
  };

  const categoryIcons = {
    travel: '✈️',
    meals: '🍽️',
    software: '💻',
    hardware: '🖥️',
    office: '🏢',
    marketing: '📢',
    other: '📦'
  };

  const categoryLabels = {
    travel: isRTL ? 'السفر' : 'Travel',
    meals: isRTL ? 'الوجبات والضيافة' : 'Meals & Entertainment',
    software: isRTL ? 'البرمجيات' : 'Software',
    hardware: isRTL ? 'الأجهزة' : 'Hardware',
    office: isRTL ? 'مستلزمات المكتب' : 'Office Supplies',
    marketing: isRTL ? 'التسويق' : 'Marketing',
    other: isRTL ? 'أخرى' : 'Other'
  };

  useEffect(() => {
    fetchExpenses();
    fetchProjects();
  }, []);

  const fetchExpenses = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pm/expenses`);
      setExpenses(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pm/projects`);
      setProjects(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingExpense) {
        await axios.put(`${API_URL}/api/pm/expenses/${editingExpense.id}`, formData);
        toast.success(isRTL ? 'تم تحديث المصروف' : 'Expense updated');
      } else {
        await axios.post(`${API_URL}/api/pm/expenses`, formData);
        toast.success(isRTL ? 'تم إضافة المصروف' : 'Expense added');
      }
      setShowDialog(false);
      setEditingExpense(null);
      setFormData({});
      fetchExpenses();
    } catch (err) {
      toast.error(isRTL ? 'فشل حفظ المصروف' : 'Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRTL ? 'حذف هذا المصروف؟' : 'Delete this expense?')) {
      try {
        await axios.delete(`${API_URL}/api/pm/expenses/${id}`);
        toast.success(isRTL ? 'تم حذف المصروف' : 'Expense deleted');
        fetchExpenses();
      } catch (err) {
        toast.error(isRTL ? 'فشل الحذف' : 'Failed to delete');
      }
    }
  };

  const updateStatus = async (expenseId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/pm/expenses/${expenseId}`, { status: newStatus });
      toast.success(isRTL ? 'تم تحديث الحالة' : 'Status updated');
      fetchExpenses();
    } catch (err) {
      toast.error(isRTL ? 'فشل التحديث' : 'Failed to update');
    }
  };

  const openEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({ ...expense, date: expense.date?.split('T')[0] || '' });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingExpense(null);
    setFormData({ category: 'other', currency: 'USD' });
    setShowDialog(true);
  };

  const getProjectName = (projectId) => projects.find(p => p.id === projectId)?.name || '-';

  const filteredExpenses = expenses.filter(e => 
    filterCategory === 'all' || e.category === filterCategory
  );

  const stats = {
    total: expenses.reduce((sum, e) => sum + (e.amount || 0), 0),
    pending: expenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + (e.amount || 0), 0),
    approved: expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + (e.amount || 0), 0),
    reimbursed: expenses.filter(e => e.status === 'reimbursed').reduce((sum, e) => sum + (e.amount || 0), 0)
  };

  return (
    <div className="space-y-6">
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.expenses.title')}</h1>
          <p className="text-muted-foreground">{t('pages.expenses.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Plus className="h-4 w-4" /> {t('pages.expenses.addExpense')}
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold"><CurrencyValue value={stats.total.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div><div className="text-sm text-muted-foreground">{t('pages.common.total')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-amber-600"><CurrencyValue value={stats.pending.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div><div className="text-sm text-muted-foreground">{t('dashboard.pending')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600"><CurrencyValue value={stats.approved.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div><div className="text-sm text-muted-foreground">{statusLabels.approved}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600"><CurrencyValue value={stats.reimbursed.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div><div className="text-sm text-muted-foreground">{statusLabels.reimbursed}</div></CardContent></Card>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48"><SelectValue placeholder={t('pages.common.filter')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isRTL ? 'جميع الفئات' : 'All Categories'}</SelectItem>
            {Object.entries(categoryLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>{categoryIcons[key]} {label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.expenses.description')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.expenses.category')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'المشروع' : 'Project'}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.expenses.date')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.expenses.amount')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'الحالة' : 'Status'}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredExpenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell className={isRTL ? 'text-right' : ''}>
                  <div className="font-medium">{expense.description}</div>
                  {expense.notes && <div className="text-xs text-muted-foreground">{expense.notes}</div>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {categoryIcons[expense.category]} {categoryLabels[expense.category]}
                  </Badge>
                </TableCell>
                <TableCell className={`text-muted-foreground ${isRTL ? 'text-right' : ''}`}>{getProjectName(expense.project_id)}</TableCell>
                <TableCell className={`text-muted-foreground ${isRTL ? 'text-right' : ''}`}>{format(new Date(expense.date), 'MMM d, yyyy')}</TableCell>
                <TableCell className="font-mono font-semibold"><CurrencyValue value={expense.amount?.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} size={14} /></TableCell>
                <TableCell><Badge className={statusColors[expense.status]}>{statusLabels[expense.status]}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(expense)}>
                        <Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => updateStatus(expense.id, 'approved')}>
                        <CheckCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'موافقة' : 'Approve'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateStatus(expense.id, 'rejected')}>
                        <XCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'رفض' : 'Reject'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateStatus(expense.id, 'reimbursed')}>
                        <Receipt className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'سداد' : 'Reimburse'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(expense.id)}>
                        <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredExpenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <Wallet className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">{isRTL ? 'لا توجد مصروفات' : 'No expenses found'}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className={`max-w-md ${isRTL ? 'text-right' : ''}`}>
          <DialogHeader><DialogTitle>{editingExpense ? (isRTL ? 'تعديل المصروف' : 'Edit Expense') : t('pages.expenses.addExpense')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('pages.expenses.description')} *</Label>
              <Input value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} placeholder={isRTL ? 'مستلزمات مكتبية، سفر...' : 'Office supplies, travel, etc.'} className={isRTL ? 'text-right' : ''} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.expenses.amount')} *</Label>
                <Input type="number" value={formData.amount || ''} onChange={e => setFormData({...formData, amount: parseFloat(e.target.value) || 0})} placeholder="0.00" className={isRTL ? 'text-right' : ''} />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.expenses.date')}</Label>
                <Input type="date" value={formData.date || ''} onChange={e => setFormData({...formData, date: e.target.value})} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.expenses.category')}</Label>
                <Select value={formData.category || 'other'} onValueChange={v => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{categoryIcons[key]} {label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'المشروع' : 'Project'}</Label>
                <Select value={formData.project_id || ''} onValueChange={v => setFormData({...formData, project_id: v})}>
                  <SelectTrigger><SelectValue placeholder={isRTL ? 'بدون' : 'None'} /></SelectTrigger>
                  <SelectContent>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} placeholder={isRTL ? 'تفاصيل إضافية...' : 'Additional details...'} className={isRTL ? 'text-right' : ''} />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('pages.common.cancel')}</Button>
            <Button onClick={handleSave}>{editingExpense ? (isRTL ? 'تحديث' : 'Update') : t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
